import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserLoginAction, SetIPhoneModeAction, resetPasswordAction, LogOutAction } from "../../stores/actions/userAction";
import { Eye, EyeOff, Lock, Phone, RefreshCw, KeyRound, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import iaimsAppLogo from "../../assets/iaims-logo.png";
import axios from "axios";
import { baseUrl } from "../../environment";
import { StoreState } from "../../models/reduxModels";
import { useSnackbar } from "notistack";

type LoginViewMode = "login" | "forgot_password_otp" | "forgot_password_verify" | "reset_password_forced";

const IPhoneMobileLogin: React.FC = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state: StoreState) => state.user.userDetails);

  // Determine if forced password reset is required
  const rawResetFlag = (user as any)?.is_reset_password;
  const isForceReset = !!user && (rawResetFlag === 1 || rawResetFlag === true || String(rawResetFlag) === "1" || String(rawResetFlag) === "true");

  const [viewMode, setViewMode] = useState<LoginViewMode>(
    isForceReset ? "reset_password_forced" : "login"
  );

  // Form states
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password states
  const [forgotUserId, setForgotUserId] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Force reset states
  const [oldPassword, setOldPassword] = useState("");

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Helper to extract actual error message returned by backend API
  const getBackendErrorMessage = (err: any, fallback: string): string => {
    if (typeof err?.response?.data?.message === "string" && err.response.data.message.trim()) {
      return err.response.data.message.trim();
    }
    if (typeof err?.response?.data?.error === "string" && err.response.data.error.trim()) {
      return err.response.data.error.trim();
    }
    if (typeof err?.response?.data === "string" && err.response.data.trim()) {
      return err.response.data.trim();
    }
    if (typeof err?.data?.message === "string" && err.data.message.trim()) {
      return err.data.message.trim();
    }
    if (typeof err?.message === "string" && err.message.trim() && !err.message.startsWith("Request failed with status code")) {
      return err.message.trim();
    }
    return fallback;
  };

  // Standard Login
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    const trimmedUser = userId.trim();
    const trimmedPass = password.trim();

    if (!trimmedUser) {
      setError("Please enter your registered mobile number or User ID.");
      return;
    }
    if (!trimmedPass) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);

    const payload = {
      userid: trimmedUser,
      password: trimmedPass,
      platform: "app",
    };

    dispatch(UserLoginAction(payload) as any)
      .then((resData: any) => {
        setLoading(false);
        if (resData?.is_reset_password === 1 || resData?.is_reset_password === true) {
          setViewMode("reset_password_forced");
        }
      })
      .catch((err: any) => {
        setLoading(false);
        setError(getBackendErrorMessage(err, "Login failed. Please verify credentials."));
      });
  };

  // Step 1: Send OTP for Forgot Password
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!forgotUserId.trim()) {
      setError("Please enter your User ID / Mobile number.");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(baseUrl + "users/send-password-reset-otp", {
        employee_id: forgotUserId.trim(),
      });

      setLoading(false);
      if (res.status === 200) {
        setSuccessMsg("OTP sent successfully to your registered mobile number.");
        setViewMode("forgot_password_verify");
      } else {
        setError(res.data?.message || "Failed to send OTP. Please try again.");
      }
    } catch (err: any) {
      setLoading(false);
      setError(getBackendErrorMessage(err, "Failed to send OTP. Please verify your User ID."));
    }
  };

  // Step 2: Verify OTP & Reset Password
  const handleVerifyOtpAndReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!otp.trim()) {
      setError("Please enter the OTP.");
      return;
    }
    if (!newPassword.trim()) {
      setError("Please enter a new password.");
      return;
    }
    if (newPassword.trim() !== confirmPassword.trim()) {
      setError("New password and confirm password do not match.");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(baseUrl + "users/verify-otp-reset-password", {
        // employee_id: forgotUserId.trim(),
        employee_id: user?.contact || forgotUserId?.trim(),
        otp: otp.trim(),
        password: newPassword.trim(),

      });

      setLoading(false);
      if (res.status === 200) {
        setSuccessMsg("Password reset successfully! Please sign in with your new password.");
        setViewMode("login");
        setUserId(forgotUserId.trim());
        setPassword("");
        setForgotUserId("");
        setOtp("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setError(res.data?.message || "OTP verification failed. Please try again.");
      }
    } catch (err: any) {
      setLoading(false);
      setError(getBackendErrorMessage(err, "Failed to verify OTP or reset password."));
    }
  };

  // Forced Initial Password Reset Handler
  const handleForcedResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!oldPassword.trim()) {
      setError("Please enter your current password.");
      return;
    }
    if (!newPassword.trim()) {
      setError("Please enter a new password.");
      return;
    }
    if (newPassword.trim() !== confirmPassword.trim()) {
      setError("New password and confirm password do not match.");
      return;
    }

    setLoading(true);

    const resetPayload = {
      userid: user?.contact || userId?.trim(),
      old_password: oldPassword.trim(),
      new_password: newPassword.trim(),
    };

    dispatch(resetPasswordAction(resetPayload, { enqueueSnackbar }) as any)
      .then(() => {
        setLoading(false);
        dispatch(LogOutAction() as any);
        setViewMode("login");
        setSuccessMsg("Password updated successfully. Please sign in with your new password.");
      })
      .catch((err: any) => {
        setLoading(false);
        setError(getBackendErrorMessage(err, "Failed to reset password. Please try again."));
      });
  };

  const handleSwitchMode = () => {
    dispatch(SetIPhoneModeAction(undefined));
  };

  return (
    <div className="min-h-[100dvh] w-full bg-gradient-to-b from-[#FFFBF5] to-[#FFF7ED] flex flex-col justify-between p-4 sm:p-6 font-sans selection:bg-[#F97316] selection:text-white">
      {/* Top Switch Mode Link */}
      <div className="w-full flex justify-end max-w-md mx-auto pt-safe pt-2">
        <button
          type="button"
          onClick={handleSwitchMode}
          className="text-[11px] sm:text-xs font-semibold text-[#EA580C] bg-white/80 hover:bg-white border border-[#FED7AA] px-3 py-1.5 rounded-full shadow-xs flex items-center space-x-1 transition-all active:scale-95"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Switch Mode</span>
        </button>
      </div>

      {/* Main Form Container */}
      <div className="w-full max-w-md mx-auto my-auto py-4 sm:py-6 space-y-5 sm:space-y-6">
        {/* IAIMS Mobile App Logo & Branding */}
        <div className="flex flex-col items-center text-center space-y-2.5 sm:space-y-3">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white border-2 border-white/60 p-2 shadow-xl flex items-center justify-center overflow-hidden shrink-0">
            <img
              src={iaimsAppLogo}
              alt="IAIMS App Logo"
              className="w-full h-full object-contain rounded-xl"
            />
          </div>

          <div>
            <h1 className="text-lg sm:text-xl font-black text-[#F97316] tracking-tight">
              IAIMS
            </h1>
            <p className="text-[11px] sm:text-xs font-bold text-gray-800 mt-1 max-w-[260px] mx-auto leading-tight">
              Integrated Attendance & Identity Management System
            </p>
            <p className="text-[10px] sm:text-[11px] text-gray-500 font-medium mt-1 max-w-[240px] mx-auto leading-tight">
              Secure Identity. Verified Attendance. Centralized Monitoring.
            </p>
          </div>
        </div>

        {/* Mobile Login / Forgot Password Card */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl p-5 sm:p-6 shadow-xl border border-white/40 space-y-4 sm:space-y-5">
          {error && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 text-xs font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* VIEW MODE 1: Standard Login */}
          {viewMode === "login" && (
            <>
              <div className="space-y-0.5">
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Welcome Back</h2>
                <p className="text-[11px] sm:text-xs text-gray-500">Sign in to your attendance account</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-3.5 sm:space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    User ID / Mobile
                  </label>
                  <div className="relative flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="text"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="Registered Mobile Number"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#F97316] focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setError("");
                        setSuccessMsg("");
                        setForgotUserId(user?.contact || userId);
                        setViewMode("forgot_password_otp");
                      }}
                      className="text-[10px] sm:text-[11px] font-bold text-[#F97316] hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#F97316] focus:bg-white transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 p-1"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#C2410C] text-white text-xs font-bold shadow-md shadow-orange-500/20 active:scale-[0.98] transition-all flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Sign In (Attendance Mode)</span>
                  )}
                </button>
              </form>
            </>
          )}

          {/* VIEW MODE 2: Forgot Password - Step 1 (Request OTP) */}
          {viewMode === "forgot_password_otp" && (
            <>
              <div className="flex items-center space-x-2 mb-1">
                <button
                  onClick={() => {
                    setError("");
                    setSuccessMsg("");
                    setViewMode("login");
                  }}
                  className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-gray-900">Forgot Password</h2>
                  <p className="text-[11px] sm:text-xs text-gray-500">Request password reset OTP</p>
                </div>
              </div>

              <form onSubmit={handleSendOtp} className="space-y-3.5">
                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    User ID / Mobile
                  </label>
                  <div className="relative flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="text"
                      value={forgotUserId}
                      onChange={(e) => setForgotUserId(e.target.value)}
                      placeholder="Registered User ID / Mobile"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316] focus:bg-white"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold shadow-md active:scale-[0.98] transition-all flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Send Reset OTP</span>
                  )}
                </button>
              </form>
            </>
          )}

          {/* VIEW MODE 3: Forgot Password - Step 2 (Verify OTP & Set Password) */}
          {viewMode === "forgot_password_verify" && (
            <>
              <div className="flex items-center space-x-2 mb-1">
                <button
                  onClick={() => setViewMode("forgot_password_otp")}
                  className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-gray-900">Verify OTP & Reset</h2>
                  <p className="text-[11px] sm:text-xs text-gray-500">Enter OTP & set your new password</p>
                </div>
              </div>

              <form onSubmit={handleVerifyOtpAndReset} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    Enter OTP
                  </label>
                  <div className="relative flex items-center">
                    <KeyRound className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="6-digit OTP"
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316]"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    New Password
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New Password"
                      className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 p-1"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    Confirm New Password
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm New Password"
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316]"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold shadow-md active:scale-[0.98] transition-all flex items-center justify-center space-x-2 mt-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Reset Password Now</span>
                  )}
                </button>
              </form>
            </>
          )}

          {/* VIEW MODE 4: Forced Initial Reset Password (is_reset_password = 1) */}
          {viewMode === "reset_password_forced" && (
            <>
              <div className="space-y-0.5">
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Reset Your Password</h2>
                <p className="text-[11px] sm:text-xs text-gray-500">First-time login requirement</p>
              </div>

              <form onSubmit={handleForcedResetPassword} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    Current Password
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="Current Password"
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316]"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    New Password
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New Password"
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316]"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] sm:text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    Confirm Password
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm New Password"
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-[#F97316]"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold shadow-md active:scale-[0.98] transition-all flex items-center justify-center space-x-2 mt-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Update Password & Continue</span>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IPhoneMobileLogin;
